// =============================================================================
// VandieZ admin-web -- SMS dispatch log (/settings/sms/log)
// =============================================================================
// Paginated table of SMS dispatch events with event_key + status filters.
// Mirrors the DisputesPage idiom: useState + useEffect, no react-query.
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  MessageSquare,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { smsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { SmsDispatchLogRow } from "@/types/sms";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Queued", value: "queued" },
  { label: "Sent", value: "sent" },
  { label: "Delivered", value: "delivered" },
  { label: "Failed", value: "failed" },
  { label: "Skipped", value: "skipped" },
];

const STATUS_PILL: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  sent: "bg-primary-subtle text-primary-subtle-foreground",
  delivered: "bg-success-subtle text-success-subtle-foreground",
  failed: "bg-danger-subtle text-danger-subtle-foreground",
  skipped: "bg-warning-subtle text-warning-subtle-foreground",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function SmsLogPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<SmsDispatchLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);

  const [filterEvent, setFilterEvent] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) { return; }
      setLoading(true);
      setError(null);
      try {
        const res = await smsAdminApi.listLog(
          accessToken,
          {
            limit: pageSize,
            offset: nextOffset,
            ...(filterEvent.trim() !== "" ? { event_key: filterEvent.trim() } : {}),
            ...(filterStatus !== "" ? { status: filterStatus } : {}),
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setOffset(res.offset);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") { return; }
        const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filterEvent, filterStatus, pageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(0, controller.signal);
    return () => { controller.abort(); };
  }, [fetchPage]);

  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const res = await smsAdminApi.listLog(accessToken, {
        limit: 200,
        offset: 0,
        ...(filterEvent.trim() !== "" ? { event_key: filterEvent.trim() } : {}),
        ...(filterStatus !== "" ? { status: filterStatus } : {}),
      });
      const header = [
        "sent_at",
        "event_key",
        "phone",
        "language",
        "provider",
        "status",
        "cost_inr",
        "error_code",
      ];
      const escape = (v: string): string =>
        /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = [
        header.join(","),
        ...res.items.map((r) =>
          [
            r.sent_at,
            r.event_key,
            r.phone,
            r.language,
            r.provider,
            r.status,
            r.cost_inr ?? "",
            r.error_code ?? "",
          ]
            .map((c) => escape(String(c)))
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vandiez-sms-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="SMS dispatch log"
        description="Individual SMS dispatch records. Filter by event or delivery status."
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => void exportCsv()}
              disabled={exporting || items.length === 0}
            >
              <Download className="mr-1 h-4 w-4" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Link
              to="/settings/sms"
              className="text-sm text-muted-foreground hover:underline"
            >
              ← SMS settings
            </Link>
          </div>
        }
      />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" /> Dispatch log
            </CardTitle>
            <CardDescription>
              Individual SMS dispatch records. Filter by event or delivery status.
              {total > 0 && (
                <span className="ml-1 text-foreground">
                  Showing {offset + 1}–{offset + items.length} of {total}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* filters */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Event:</span>
                <input
                  type="text"
                  value={filterEvent}
                  onChange={(e) => { setFilterEvent(e.target.value); }}
                  placeholder="e.g. otp_request"
                  className="rounded border bg-background px-2 py-1 text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">Status:</span>
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setFilterStatus(opt.value); }}
                    className={cn(
                      "rounded px-3 py-1 text-xs",
                      filterStatus === opt.value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading && items.length === 0 ? (
              <TableSkeleton columns={8} />
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                No records match the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Sent at</th>
                      <th className="py-2 pr-4">Event</th>
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">Lang</th>
                      <th className="py-2 pr-4">Provider</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Cost</th>
                      <th className="py-2 pr-4">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b last:border-b-0 hover:bg-muted/40 cursor-pointer"
                        onClick={() => { navigate(`/settings/sms/log/${row.id}`); }}
                      >
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {formatDate(row.sent_at)}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          {row.event_key}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs">
                          {row.phone}
                        </td>
                        <td className="py-3 pr-4 text-xs uppercase">
                          {row.language}
                        </td>
                        <td className="py-3 pr-4 text-xs">{row.provider}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                              STATUS_PILL[row.status] ?? "bg-muted text-muted-foreground",
                            )}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-xs tabular-nums">
                          {row.cost_inr !== null ? `₹${row.cost_inr}` : "—"}
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {row.error_code ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => void fetchPage(Math.max(0, offset - pageSize))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
                </span>
                <label className="flex items-center gap-1">
                  Rows:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number.parseInt(e.target.value, 10));
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => void fetchPage(offset + pageSize)}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
